import type { KeyboardEvent, ReactNode } from 'react'
import { Check } from 'lucide-react'
import { Badge } from '@/shared/ui/primitives'
import type { ProPlanChoice as PlanChoice } from '../types'

export type ProPlanChoiceProps = {
  selection: PlanChoice
  proPriceLabel: string
  wuPriceLabel: string
  onChange: (plan: PlanChoice) => void
  onShowProFeatures: () => void
  proBillingSlot?: ReactNode
}

type OptionProps = {
  active: boolean
  title: string
  priceLabel: string
  description: string
  testId: string
  badge?: ReactNode
  children?: ReactNode
  onSelect: () => void
}

function PlanOption({
  active,
  title,
  priceLabel,
  description,
  testId,
  badge,
  children,
  onSelect,
}: OptionProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onSelect()
  }

  return (
    <div
      role="radio"
      aria-checked={active}
      tabIndex={0}
      data-testid={testId}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={`flex w-full cursor-pointer flex-col gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
        active ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
            active
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-transparent'
          }`}
        >
          <Check className="size-3" strokeWidth={3} />
        </span>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-base font-semibold text-foreground">{title}</span>
            <span className="whitespace-nowrap text-base font-bold text-foreground">
              {priceLabel}
            </span>
          </div>
          {badge ? <div className="flex">{badge}</div> : null}
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

export function ProPlanChoice({
  selection,
  proPriceLabel,
  wuPriceLabel,
  onChange,
  onShowProFeatures,
  proBillingSlot,
}: ProPlanChoiceProps) {
  return (
    <div className="flex flex-col gap-3" data-testid="pro-plan-choice">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-foreground">
        Choose your plan
      </p>
      <div role="radiogroup" aria-label="Choose your plan" className="flex flex-col gap-3">
        <PlanOption
          testId="pro-plan-pro"
          active={selection === 'pro'}
          title="BlindBook Pro"
          priceLabel={proPriceLabel}
          description="Everything in Free, plus all Pro features."
          onSelect={() => onChange('pro')}
        >
          <button
            type="button"
            data-testid="pro-see-features"
            onClick={(event) => {
              event.stopPropagation()
              onShowProFeatures()
            }}
            className="ml-8 self-start text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            See all Pro features
          </button>
          {proBillingSlot}
        </PlanOption>
        <PlanOption
          testId="pro-plan-wu"
          active={selection === 'wu'}
          title="Waterfowl U"
          priceLabel={wuPriceLabel}
          description="Every module, lesson, and quiz — a full season of instruction filmed on real hunts."
          badge={
            <Badge>
              <Check aria-hidden className="mr-1 size-3" strokeWidth={3} />
              Includes BlindBook Pro
            </Badge>
          }
          onSelect={() => onChange('wu')}
        />
      </div>
    </div>
  )
}
